import dayjs from "dayjs";
import { MemberProfile } from "../../../../model/members/MemberProfile";
// validFrom / validTo are DateOnly on the wire (C# `DateOnly` -> "YYYY-MM-DD"), which
// DateReviver turns into a Date at UTC midnight. Reading that with dayjs() directly
// names the PREVIOUS day in any timezone behind UTC, so every calculation below —
// days left, period start, the validity meter — would be a day out for a club west of
// Greenwich. fromDateOnly rebuilds the value at local midnight from its UTC fields.
import { fromDateOnly } from "../../../../utils/DateOnly";

export type StatusTone = "ok" | "warn" | "err";

export interface StatusMeta {
    tone: StatusTone;
    // Maps to an Ant Tag/Alert colour.
    color: "success" | "warning" | "error";
    text: string;
}

// Whole days from today until the membership's validTo (negative once expired).
export function daysLeft(member: MemberProfile): number {
    return fromDateOnly(member.validTo).startOf("day").diff(dayjs().startOf("day"), "day");
}

// Start of the member's CURRENT membership period — the basis for the validity
// meter. Monthly memberships roll over each month, so the meaningful window is
// the current month (the first of validTo's month), or validFrom if they joined
// later. Annual memberships run for a year up to validTo, or from validFrom if
// that's later. Without this, a long-running rolled-over monthly member would
// show a meter spanning years and pinned near 100%.
export function periodStart(member: MemberProfile): dayjs.Dayjs {
    const to = fromDateOnly(member.validTo);
    const isAnnual = member.membershipCategory?.paymentFrequency === "Annual";
    const naturalStart = isAnnual ? to.subtract(1, "year") : to.startOf("month");
    const from = member.validFrom ? fromDateOnly(member.validFrom) : null;
    return from && from.isAfter(naturalStart) ? from : naturalStart;
}

// How far through the CURRENT period we are, clamped 0–100. Falls back to a
// sensible value when the window is zero-length or dates are missing.
export function elapsedPct(member: MemberProfile): number {
    if (!member.validTo) return 0;
    const start = periodStart(member);
    const to = fromDateOnly(member.validTo);
    const total = to.diff(start, "day");
    if (total <= 0) {
        return daysLeft(member) < 0 ? 100 : 0;
    }
    const elapsed = dayjs().diff(start, "day");
    return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
}

// Expiry is "urgent" (escalated alert) within 14 days; the validity meter turns
// amber within 30 days.
export const EXPIRY_URGENT_DAYS = 14;
export const EXPIRY_WARN_DAYS = 30;

// Legacy imported statuses that mean the membership has ended.
const EXPIRED_STATUSES = ["expired", "lapsed"];

// Whether the membership has expired: past its valid-to, in the "Expired"
// membership category (where imports park unmapped members and staff move a
// leaver), or carrying a legacy Expired / Lapsed status. An enabled, in-date
// member in any of those used to read as Active (FORT-454).
export function isExpired(member: MemberProfile): boolean {
    const category = (member.membershipCategory?.name ?? "").trim().toLowerCase();
    const status = (member.status ?? "").trim().toLowerCase();
    return category === "expired" || EXPIRED_STATUSES.includes(status) || daysLeft(member) < 0;
}

// Status pill / stripe tone for the identity header and membership card.
export function statusMeta(member: MemberProfile): StatusMeta {
    const left = daysLeft(member);
    if (!member.isEnabled || member.status === "Cancelled" || member.status === "Banned") {
        return { tone: "err", color: "error", text: member.status || "Inactive" };
    }
    if (isExpired(member)) {
        return { tone: "err", color: "error", text: "Expired" };
    }
    if (left <= EXPIRY_URGENT_DAYS) {
        return { tone: "warn", color: "warning", text: "Active" };
    }
    return { tone: "ok", color: "success", text: "Active" };
}
